import { useState } from "react";
import AuthInputs from "./components/AuthInputs";
import CheckboxRegular from "./components/CheckboxRegular";
import CheckboxRegularPrivacy from "./components/CheckboxRegularPrivacy";
import CheckboxRegularTerms from "./components/CheckboxRegularTerms";
import BrownSubmit from "../../buttons/BrownSubmit";
import {
  signupValidation,
  invalidPasswordMessage,
} from "../../../services/validation/authValidation";
import { useRouter } from "next/router";
import { login, signup } from "../../../services/cms/auth";
import passwordValidator from "password-validator";
import ReCAPTCHA from "react-google-recaptcha";
import MailIcon from "../../icons/auth_inputs/mail.svg";
import LockIcon from "../../icons/auth_inputs/lock.svg";
import PersonIcon from "../../icons/auth_inputs/person.svg";
import PhoneIcon from "../../icons/auth_inputs/phone.svg";

const EMPTY_FIELD_ERRORS = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  password1: "",
  password2: "",
  terms: "",
  privacy: "",
  recaptcha: "",
};

const SignupForm = ({ setError, setVisible }) => {
  const [recaptcha, setRecaptcha] = useState("");
  const [fieldErrors, setFieldErrors] = useState(EMPTY_FIELD_ERRORS);

  const onRecaptchaChange = (value) => {
    setRecaptcha(value || "");
    if (value) {
      setFieldErrors((prev) => ({ ...prev, recaptcha: "" }));
    }
  };

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(false);

  const router = useRouter();

  const schema = new passwordValidator();
  const schemaEmail = new passwordValidator();
  const schemaPhone = new passwordValidator();

  schema
    .is()
    .min(8)
    .is()
    .max(100)
    .has()
    .uppercase()
    .has()
    .lowercase()
    .has()
    .digits(1)
    .has()
    .symbols(1, ["!", "@", "#", "$", "%", "^", "&", "*"])
    .has()
    .not()
    .spaces();

  schemaEmail.has(["@"]);
  schemaPhone.has().digits(10);

  const showFormError = (message) => {
    setError(message);
    setVisible(true);
  };

  const clearFieldError = (field) => {
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const onSubmitForm = async (event) => {
    event.preventDefault();
    setFieldErrors(EMPTY_FIELD_ERRORS);

    const normalizedEmail = email.trim().toLowerCase();
    const fullData = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      username: normalizedEmail,
      email: normalizedEmail,
      phone: phone.trim(),
      password1,
      password2,
      newsletter,
      privacy,
      terms,
      recaptcha,
      password: password1,
    };

    const nextFieldErrors = { ...EMPTY_FIELD_ERRORS };
    let bannerError = null;

    const requiredCheck = signupValidation(fullData);
    if (requiredCheck.error) {
      if (requiredCheck.field) {
        nextFieldErrors[requiredCheck.field] = requiredCheck.error;
      }
      bannerError = requiredCheck.error;
    }

    const validatedPassword = schema.validate(password1, { list: true });
    const validatedEmail = schemaEmail.validate(normalizedEmail, { list: true });
    const validatedPhone = schemaPhone.validate(phone.trim(), { list: true });

    if (validatedPassword.length > 0) {
      const passwordErrorList = invalidPasswordMessage(validatedPassword);
      nextFieldErrors.password1 = passwordErrorList[0];
      bannerError = bannerError || (
        <ul>
          {passwordErrorList.map((mess, index) => (
            <li key={index}>{mess}</li>
          ))}
        </ul>
      );
    }

    if (validatedEmail.length > 0) {
      nextFieldErrors.email = "Please provide a valid email address.";
      bannerError = bannerError || "Please provide a valid email address.";
    }

    if (validatedPhone.length > 0) {
      nextFieldErrors.phone =
        "Please provide your phone number in the following format: 1231231234.";
      bannerError =
        bannerError ||
        "Please provide your phone number in the following format: 1231231234.";
    }

    if (!recaptcha) {
      nextFieldErrors.recaptcha = "Please complete the reCAPTCHA to continue.";
      bannerError = bannerError || "Please complete the reCAPTCHA to continue.";
    }

    const hasFieldErrors = Object.values(nextFieldErrors).some(Boolean);
    if (hasFieldErrors) {
      setFieldErrors(nextFieldErrors);
      showFormError(
        bannerError || "Please correct the errors below before continuing.",
      );
      return;
    }

    const response = await signup(fullData);
    if (response?.status === 201) {
      login(fullData);
      router.push("/onboarding/profile");
    } else {
      try {
        const errorMessage = await response.json();
        showFormError(errorMessage.status_message);
      } catch (err) {
        console.log(err);
        showFormError(
          "Oops, something went wrong, and we're not sure what. Please reach out to info@thebohomarket.com",
        );
      }
    }
  };

  return (
    <form className="signup-form" noValidate onSubmit={onSubmitForm}>
      <div className="signup_Format">
        <div>
          <AuthInputs
            label="First Name"
            value={firstName}
            setValue={(value) => {
              setFirstName(value);
              clearFieldError("firstName");
            }}
            type="text"
            imageAlt="person icon"
            imageURL={PersonIcon}
            htmlFor="first_name"
            placeholder="First name"
            error={fieldErrors.firstName}
          />
          <AuthInputs
            label="Last Name"
            value={lastName}
            setValue={(value) => {
              setLastName(value);
              clearFieldError("lastName");
            }}
            type="text"
            imageAlt="person icon"
            imageURL={PersonIcon}
            htmlFor="last_name"
            placeholder="Last name"
            error={fieldErrors.lastName}
          />
          <AuthInputs
            label="Phone Number"
            value={phone}
            setValue={(value) => {
              setPhone(value);
              clearFieldError("phone");
            }}
            type="tel"
            imageAlt="phone icon"
            imageURL={PhoneIcon}
            htmlFor="phone"
            placeholder="1231231234"
            error={fieldErrors.phone}
          />
          <AuthInputs
            label="Email"
            value={email}
            setValue={(value) => {
              setEmail(value);
              clearFieldError("email");
            }}
            type="text"
            imageAlt="mail icon"
            imageURL={MailIcon}
            htmlFor="email"
            placeholder="Email"
            error={fieldErrors.email}
          />
        </div>
        <div>
          <p
            style={{
              marginBottom: "30px",
              display: "block",
              lineHeight: "16px",
            }}
          >
            Password must:
            <ul>
              <li>have minimum 8 characters</li>
              <li>have maximum 100 characters</li>
              <li>not have any spaces</li>
              <li>include at least 1 uppercase letter</li>
              <li>include at least 1 lowercase letter</li>
              <li>include at least 1 number</li>
              <li>include at least one of the following: ! @ # % ^ &amp; *</li>
            </ul>
          </p>
          <AuthInputs
            label="Password"
            value={password1}
            setValue={(value) => {
              setPassword1(value);
              clearFieldError("password1");
            }}
            type="password"
            imageAlt="lock icon"
            imageURL={LockIcon}
            htmlFor="password1"
            placeholder="Password"
            error={fieldErrors.password1}
          />
          <AuthInputs
            label="Confirm Password"
            value={password2}
            setValue={(value) => {
              setPassword2(value);
              clearFieldError("password2");
            }}
            type="password"
            imageAlt="lock icon"
            imageURL={LockIcon}
            htmlFor="password2"
            placeholder="Confirm Password"
            error={fieldErrors.password2}
          />
        </div>
      </div>
      <div className="signup-form-bottom">
        <CheckboxRegularPrivacy
          htmlFor="privacy"
          value={privacy}
          setValue={(value) => {
            setPrivacy(value);
            clearFieldError("privacy");
          }}
          link="/privacy"
        />
        {fieldErrors.privacy ? (
          <p className="auth__checkboxError" role="alert">
            {fieldErrors.privacy}
          </p>
        ) : null}

        <CheckboxRegularTerms
          link="/terms"
          htmlFor="terms"
          value={terms}
          setValue={(value) => {
            setTerms(value);
            clearFieldError("terms");
          }}
        />
        {fieldErrors.terms ? (
          <p className="auth__checkboxError" role="alert">
            {fieldErrors.terms}
          </p>
        ) : null}

        <CheckboxRegular
          value={newsletter}
          setValue={setNewsletter}
          content="Send me the latest news and upcoming events"
        />
      </div>
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            margin: "24px auto 24px auto",
            display: "flex",
            flexDirection: "column",
            width: "20%",
          }}
        >
          <ReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
            onChange={onRecaptchaChange}
          />
          {fieldErrors.recaptcha ? (
            <p className="auth__recaptchaError" role="alert">
              {fieldErrors.recaptcha}
            </p>
          ) : null}
        </div>

        <BrownSubmit
          paddingUpDown={20}
          paddingLeftRight={158}
          type="submit"
          content="Create Account"
        />
      </div>
    </form>
  );
};

export default SignupForm;
